import {
  getUnsyncedPoints,
  markPointsSynced,
} from '../offline/gpsTrackStore';

/**
 * Envío de los puntos GPS pendientes al backend.
 *
 * Vive aquí, y no dentro de `useSurveillanceRouteGpsTracking`, porque hay DOS
 * sitios que necesitan hacerlo exactamente igual: la pantalla del mapa (que
 * puede mandar lo recién capturado sin esperar) y el guardián global
 * (`useGpsCaptureGuardian`), que es quien lo hace cuando el operario no está
 * en esa pantalla — o sea, la mayor parte de un turno.
 *
 * Duplicar estas veinte líneas garantizaba que las dos copias se
 * desincronizaran; y la parte delicada —cuándo se marca un punto como
 * enviado— es justo la que no puede tener dos versiones.
 */

/** Lo que espera la mutación `addGpsPointsBatch`. Estructural a propósito:
 *  así este módulo no depende de `useSurveillanceRoutes` y no se monta un
 *  ciclo de imports entre el hook y su propia utilidad. */
export interface GpsPointPayload {
  latitude: number;
  longitude: number;
  timestamp: string;
  speed?: number;
  accuracy?: number;
}

export type SendGpsPoints = (
  routeId: string,
  points: GpsPointPayload[]
) => Promise<unknown>;

/**
 * Manda al backend los puntos NO sincronizados de este recorrido y los marca
 * `synced` en cuanto el envío resuelve SIN throw.
 *
 * "Sin throw" incluye el encolado offline: la cola ya lo reproduce al
 * reconectar, así que reintentarlo desde aquí duplicaría puntos (ver el
 * comentario de `addGpsPointsBatch`). Un throw REAL deja los puntos sin marcar
 * y los recoge el siguiente intento — nunca se pierden, porque viven en
 * IndexedDB desde el momento de la captura.
 *
 * @returns cuántos puntos se han dado por entregados (0 si no había nada).
 */
export async function flushUnsyncedGpsPoints(
  routeId: string | undefined,
  send: SendGpsPoints
): Promise<number> {
  if (!routeId) return 0;

  const pending = await getUnsyncedPoints(routeId);
  if (pending.length === 0) return 0;

  await send(
    routeId,
    pending.map((point) => ({
      latitude: point.latitude,
      longitude: point.longitude,
      timestamp: point.timestamp,
      speed: point.speed,
      accuracy: point.accuracy,
    }))
  );

  await markPointsSynced(
    routeId,
    pending.map((point) => point.timestamp)
  );

  return pending.length;
}

export default flushUnsyncedGpsPoints;
